import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Save, Plus, Trash2, Star, Percent } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/settings")
      .then((r) => { setSettings(r.data || {}); setLoading(false); })
      .catch(() => {
        // Use defaults if API fails
        setSettings({
          phone: "+502 3779-2267",
          location: "Guatemala",
          description: "Impresión y artículos promocionales de calidad.",
          services: [
            { title: "Serigrafía Tradicional", desc: "Tintas plastisol y base agua" },
            { title: "Sublimación", desc: "Full color en poliéster" },
            { title: "DTF", desc: "Direct to Film" },
          ],
          testimonials: []
        });
        setLoading(false);
      });
  }, []);

  const update = (field, value) => setSettings((s) => ({ ...s, [field]: value }));

  const updateService = (idx, field, value) => {
    setSettings((s) => {
      const svcs = [...(s.services || [])];
      svcs[idx] = { ...svcs[idx], [field]: value };
      return { ...s, services: svcs };
    });
  };

  const addService = () => {
    setSettings((s) => ({ ...s, services: [...(s.services || []), { title: "", desc: "" }] }));
  };

  const removeService = (idx) => {
    setSettings((s) => ({ ...s, services: (s.services || []).filter((_, i) => i !== idx) }));
  };

  const updateTestimonial = (idx, field, value) => {
    setSettings((s) => {
      const t = [...(s.testimonials || [])];
      t[idx] = { ...t[idx], [field]: value };
      return { ...s, testimonials: t };
    });
  };

  const addTestimonial = () => {
    setSettings((s) => ({ ...s, testimonials: [...(s.testimonials || []), { name: "", company: "", text: "", stars: 5 }] }));
  };

  const removeTestimonial = (idx) => {
    setSettings((s) => ({ ...s, testimonials: (s.testimonials || []).filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    try {
      await API.put("/admin/settings", settings);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error al guardar");
    }
  };

  if (loading) return <div className="p-8 text-sm text-[#94A3B8]">Cargando...</div>;
  if (!settings) return <div className="p-8 text-sm text-[#94A3B8]">Error al cargar configuración</div>;

  return (
    <div className="p-6 max-w-3xl" data-testid="admin-settings-page">
      <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0047AB] mb-2">Configuración</p>
        <h1 className="text-2xl font-bold tracking-tight text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Sitio Web</h1>
      </div>

      <div className="space-y-6">
        {/* Contact info */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
          <h3 className="text-sm font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Chivo' }}>Datos de Contacto</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Teléfono</Label>
              <Input value={settings.phone || ""} onChange={(e) => update("phone", e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="setting-phone" />
            </div>
            <div>
              <Label className="text-sm font-medium text-[#0F172A]">Ubicación</Label>
              <Input value={settings.location || ""} onChange={(e) => update("location", e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="setting-location" />
            </div>
          </div>
          <div className="mt-4">
            <Label className="text-sm font-medium text-[#0F172A]">Descripción del negocio</Label>
            <Textarea value={settings.description || ""} onChange={(e) => update("description", e.target.value)} className="mt-1 border-[#CBD5E1] rounded-sm" rows={2} data-testid="setting-description" />
          </div>
        </div>

        {/* Services */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Servicios (Página principal)</h3>
            <Button variant="ghost" size="sm" onClick={addService} className="text-[#0047AB] rounded-sm" data-testid="add-service-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          <div className="space-y-3">
            {(settings.services || []).map((svc, i) => (
              <div key={i} className="flex gap-3 items-start" data-testid={`service-${i}`}>
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <Input value={svc.title} onChange={(e) => updateService(i, "title", e.target.value)} placeholder="Nombre del servicio" className="border-[#CBD5E1] rounded-sm text-sm" />
                  <Input value={svc.desc} onChange={(e) => updateService(i, "desc", e.target.value)} placeholder="Descripción corta" className="border-[#CBD5E1] rounded-sm text-sm" />
                </div>
                <Button variant="ghost" size="sm" onClick={() => removeService(i)} className="rounded-sm shrink-0 mt-1">
                  <Trash2 className="w-4 h-4 text-red-400" />
                </Button>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-[#0F172A]" style={{ fontFamily: 'Chivo' }}>Testimonios</h3>
            <Button variant="ghost" size="sm" onClick={addTestimonial} className="text-[#0047AB] rounded-sm" data-testid="add-testimonial-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </div>
          <div className="space-y-4">
            {(settings.testimonials || []).map((t, i) => (
              <div key={i} className="border border-[#E2E8F0] rounded-sm p-3 space-y-2" data-testid={`testimonial-edit-${i}`}>
                <div className="flex gap-2">
                  <Input value={t.name} onChange={(e) => updateTestimonial(i, "name", e.target.value)} placeholder="Nombre" className="border-[#CBD5E1] rounded-sm text-sm flex-1" />
                  <Input value={t.company} onChange={(e) => updateTestimonial(i, "company", e.target.value)} placeholder="Empresa" className="border-[#CBD5E1] rounded-sm text-sm flex-1" />
                  <Button variant="ghost" size="sm" onClick={() => removeTestimonial(i)} className="rounded-sm shrink-0">
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
                <Textarea value={t.text} onChange={(e) => updateTestimonial(i, "text", e.target.value)} placeholder="Testimonio del cliente..." className="border-[#CBD5E1] rounded-sm text-sm" rows={2} />
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} onClick={() => updateTestimonial(i, "stars", s)} className="p-0.5">
                      <Star className={`w-4 h-4 ${s <= (t.stars || 5) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#CBD5E1]"}`} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Discount settings */}
        <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
          <h3 className="text-sm font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Chivo' }}>Descuento por Volumen</h3>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-[#0F172A]">Activar descuento</p>
              <p className="text-xs text-[#475569]">Aplicar descuento automático por cantidad</p>
            </div>
            <Switch checked={settings.discount_enabled ?? true} onCheckedChange={(v) => update("discount_enabled", v)} data-testid="discount-enabled-toggle" />
          </div>
          {(settings.discount_enabled ?? true) && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-3 border-t border-[#E2E8F0]">
              <div>
                <Label className="text-sm font-medium text-[#0F172A]">Cantidad mínima (unidades)</Label>
                <Input type="number" min="1" value={settings.discount_min_qty ?? 300} onChange={(e) => update("discount_min_qty", parseInt(e.target.value) || 300)} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="discount-min-qty" />
                <p className="text-[10px] text-[#94A3B8] mt-1">Desde esta cantidad se aplica el descuento</p>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#0F172A]">Porcentaje de descuento (%)</Label>
                <Input type="number" min="1" max="50" value={settings.discount_percent ?? 5} onChange={(e) => update("discount_percent", parseInt(e.target.value) || 5)} className="mt-1 border-[#CBD5E1] rounded-sm" data-testid="discount-percent" />
                <p className="text-[10px] text-[#94A3B8] mt-1">Porcentaje que se descuenta del precio</p>
              </div>
            </div>
          )}
        </div>

        {/* Save */}
        <Button onClick={handleSave} className="bg-[#E30613] text-white font-bold rounded-sm px-8 py-3 hover:bg-[#C80410]" data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1" /> Guardar Cambios
        </Button>
      </div>
    </div>
  );
}
